import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePets } from '../contexts/PetContext';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';
import {
  Plus,
  Camera,
  MessageCircle,
  FileText,
  Heart,
  Users,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowRight,
  Activity,
  Loader2
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const RISK_BADGES = {
  low: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-rose-100 text-rose-800'
};

const Dashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { showToast } = useToast();
  const { assessmentsPage, assessmentsHasNext, assessmentsHasPrev, goToAssessmentsNext, goToAssessmentsPrev } = usePets();
  const { pets, loading: petsLoading } = usePets();
  const [recentAssessments, setRecentAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [stats, setStats] = useState({
    totalPets: 0,
    totalAssessments: 0,
    recentAssessments: 0,
    confidencePercent: null,
    riskLevel: null
  });

  const formatSpecies = (species) => {
    if (!species) return '';
    const key = `pets_species_${String(species).toLowerCase()}`;
    const translated = t(key);
    return translated === key ? species : translated;
  };

  useEffect(() => {
    if (pets) {
      setStats((prev) => ({
        ...prev,
        totalPets: pets.length,
        totalAssessments: pets.reduce((acc, pet) => acc + (pet.assessments?.length || 0), 0)
      }));
    }
  }, [pets]);

  useEffect(() => {
    const fetchRecentAssessments = async () => {
      if (!user) return;

      setAssessmentsLoading(true);
      try {
        const response = await axios.get('/assessments/?limit=3&ordering=-created_at');
        const items = response.data.results || [];
        setRecentAssessments(items);
        const assessmentsWithConfidence = items.filter((item) => typeof item.confidence_score === 'number');
        const avgConfidence = assessmentsWithConfidence.length
          ? Math.round(
            (assessmentsWithConfidence.reduce((sum, item) => sum + Number(item.confidence_score || 0), 0) / assessmentsWithConfidence.length) * 100
          )
          : null;
        const latestRisk = items.find((item) => item.report?.severity_level)?.report?.severity_level || null;
        setStats((prev) => ({
          ...prev,
          recentAssessments: items.length,
          confidencePercent: avgConfidence,
          riskLevel: latestRisk
        }));
      } catch (error) {
        console.error('Failed to fetch assessments:', error);
        setRecentAssessments([]);
        showToast(t('dash_recent_error'), 'error');
      } finally {
        setAssessmentsLoading(false);
      }
    };

    fetchRecentAssessments();
  }, [user, showToast, t]);

  const fallbackCopy = {
    en: {
      assess_risk_level: 'Risk level',
      assess_confidence: 'Confidence',
      dash_not_available: 'N/A',
      risk_level_low: 'Low',
      risk_level_medium: 'Medium',
      risk_level_high: 'High',
      risk_level_critical: 'Critical'
    },
    'zh-Hant': {
      assess_risk_level: '\u98a8\u96aa\u7b49\u7d1a',
      assess_confidence: 'AI \u4fe1\u5fc3\u5ea6',
      dash_not_available: '\u672a\u63d0\u4f9b',
      risk_level_low: '\u4f4e',
      risk_level_medium: '\u4e2d',
      risk_level_high: '\u9ad8',
      risk_level_critical: '\u7dca\u6025'
    },
    'zh-CN': {
      assess_risk_level: '\u98ce\u9669\u7b49\u7ea7',
      assess_confidence: 'AI \u7f6e\u4fe1\u5ea6',
      dash_not_available: '\u672a\u63d0\u4f9b',
      risk_level_low: '\u4f4e',
      risk_level_medium: '\u4e2d',
      risk_level_high: '\u9ad8',
      risk_level_critical: '\u7d27\u6025'
    }
  };

  const textWithFallback = (key) => {
    const translated = t(key);
    if (translated !== key) return translated;
    return fallbackCopy[language]?.[key] || fallbackCopy.en[key] || key;
  };

  const formatRiskLevel = (severity) => {
    const normalized = String(severity || '').toLowerCase();
    if (!normalized) return textWithFallback('dash_not_available');
    return textWithFallback(`risk_level_${normalized}`);
  };

  const riskBadge = (severity) => RISK_BADGES[String(severity || '').toLowerCase()] || 'bg-slate-100 text-slate-700';

  const quickActions = [
    {
      title: t('assess_new'),
      description: t('dash_start_first_assessment'),
      icon: Camera,
      link: '/assessment',
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      title: t('assess_chat_ai'),
      description: t('chat_subtitle'),
      icon: MessageCircle,
      link: '/chat',
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      title: t('dash_manage_pets'),
      description: t('pets_subtitle'),
      icon: Users,
      link: '/pets',
      color: 'bg-purple-500 hover:bg-purple-600'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'urgent':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'urgent':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (petsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-gray-600">{t('dash_loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-slate-200/70 bg-white/80 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {t('dash_welcome')}, {user?.first_name}
              </h2>
              <p className="mt-1 text-sm text-gray-500">{t('dash_subtitle')}</p>
            </div>
            <div className="mt-4 flex md:ml-4 md:mt-0">
              <Link
                to="/assessment"
                className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('assess_new')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl py-8 sm:px-6 lg:px-8">
        <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white/90 shadow">
            <div className="p-5">
              <div className="flex items-center">
                <Users className="h-6 w-6 flex-shrink-0 text-gray-400" />
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="truncate text-sm font-medium text-gray-500">{t('dash_total_pets')}</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.totalPets}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white/90 shadow">
            <div className="p-5">
              <div className="flex items-center">
                <FileText className="h-6 w-6 flex-shrink-0 text-gray-400" />
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="truncate text-sm font-medium text-gray-500">{t('dash_total_assessments')}</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.totalAssessments}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white/90 shadow">
            <div className="p-5">
              <div className="flex items-center">
                <Activity className="h-6 w-6 flex-shrink-0 text-gray-400" />
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="truncate text-sm font-medium text-gray-500">{textWithFallback('assess_risk_level')}</dt>
                    <dd className="text-lg font-medium text-gray-900">{formatRiskLevel(stats.riskLevel)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white/90 shadow">
            <div className="p-5">
              <div className="flex items-center">
                <Calendar className="h-6 w-6 flex-shrink-0 text-gray-400" />
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="truncate text-sm font-medium text-gray-500">{textWithFallback('assess_confidence')}</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.confidencePercent !== null ? `${stats.confidencePercent}%` : textWithFallback('dash_not_available')}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-slate-200/70 bg-white/90 shadow">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">{t('dash_quick_actions')}</h3>
                <div className="space-y-3">
                  {quickActions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <Link key={action.title} to={action.link} className={`flex items-center rounded-lg p-3 text-white transition-all duration-200 hover:shadow-md ${action.color}`}>
                        <Icon className="mr-3 h-5 w-5" />
                        <div>
                          <p className="font-medium">{action.title}</p>
                          <p className="text-sm opacity-90">{action.description}</p>
                        </div>
                        <ArrowRight className="ml-auto h-4 w-4" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-slate-200/70 bg-white/90 shadow">
              <div className="px-4 py-5 sm:p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">{t('dash_recent_assess')}</h3>
                  <Link to="/assessments" className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-500">
                    {t('dash_view_all')}
                  </Link>
                </div>
                {assessmentsLoading ? (
                  <div className="py-8 text-center">
                    <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-blue-600" />
                    <p className="text-gray-600">{t('dash_loading_assess')}</p>
                  </div>
                ) : recentAssessments.length > 0 ? (
                  <div className="flow-root">
                    <ul className="-my-5 divide-y divide-gray-200">
                      {recentAssessments.map((assessment) => (
                        <li key={assessment.id} className="py-4">
                          <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0">{getStatusIcon(assessment.status)}</div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between">
                                <p className="truncate text-sm font-medium text-gray-900">
                                  {assessment.pet?.name || t('dash_unknown_pet')} - {assessment.assessment_type || t('dash_health_check')}
                                </p>
                                <div className="flex items-center space-x-2">
                                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(assessment.status)}`}>
                                    {assessment.status}
                                  </span>
                                  <span className="text-sm text-gray-500">{new Date(assessment.created_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                              <div className="mt-1 flex items-center space-x-4">
                                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${riskBadge(assessment.report?.severity_level)}`}>
                                    {textWithFallback('assess_risk_level')}: {formatRiskLevel(assessment.report?.severity_level)}
                                  </span>
                                  <div className="flex items-center text-sm text-gray-500">
                                    <Heart className="mr-1 h-4 w-4" />
                                    {textWithFallback('assess_confidence')}: {typeof assessment.confidence_score === 'number' ? `${Math.round(assessment.confidence_score * 100)}%` : textWithFallback('dash_not_available')}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <FileText className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                    <h3 className="mb-2 text-lg font-medium text-gray-900">{t('dash_no_assess')}</h3>
                    <p className="mb-4 text-gray-600">{t('dash_start_first_assessment')}</p>
                    <Link
                      to="/assessment"
                      className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {t('dash_start_assess')}
                    </Link>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-center space-x-3">
                  <button onClick={goToAssessmentsPrev} disabled={!assessmentsHasPrev} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-700 disabled:opacity-50">
                    {t('back')}
                  </button>
                  <span className="text-sm text-gray-600">{t('page')} {assessmentsPage}</span>
                  <button onClick={goToAssessmentsNext} disabled={!assessmentsHasNext} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-700 disabled:opacity-50">
                    {t('next')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {pets && pets.length > 0 && (
          <div className="mt-6">
            <div className="rounded-2xl border border-slate-200/70 bg-white/90 shadow">
              <div className="px-4 py-5 sm:p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">{t('dash_your_pets')}</h3>
                  <Link to="/pets" className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-500">
                    {t('dash_manage_pets')}
                  </Link>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {pets.slice(0, 3).map((pet) => (
                    <div key={pet.id} className="rounded-xl border border-slate-200 bg-white/80 p-4 transition-shadow hover:shadow-md">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                            <Heart className="h-6 w-6 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <h4 className="text-sm font-medium text-gray-900">{pet.name}</h4>
                          <p className="text-sm text-gray-500">{formatSpecies(pet.species)}{pet.breed ? ` · ${pet.breed}` : ''}</p>
                          <p className="text-xs text-gray-400">{t('pets_age')}: {pet.age}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {pets.length > 3 && (
                  <div className="mt-4 text-center">
                    <Link to="/pets" className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-500">
                      {t('dash_view_all_pets', { count: pets.length })}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {(!pets || pets.length === 0) && (
          <div className="mt-6">
            <div className="rounded-2xl border border-slate-200/70 bg-white/90 shadow">
              <div className="px-4 py-12 text-center sm:p-6">
                <Heart className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('dash_no_pets')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('dash_start_first_assessment')}</p>
                <div className="mt-6">
                  <Link
                    to="/pets"
                    className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('dash_add_first_pet')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
